import React from "react";
import FullScreenPanel from "../../../ui/FullScreenPanel.jsx";
import { fmtQty, lineBack, lineRemaining, vendorOverride } from "../../../../features/outsourcing/coatingData";
import { LineIdentity } from "./ManifestCards.jsx";

// The phone Outsourcing tab's two forms as full-screen panels (the desktop's Create Manifest and Receive modals in
// operator mode). Layout only: OutsourcingTab owns the state and calls features/outsourcing/coatingActions.

/** Make the paper from the ticked pallets / jobs (vendor, due back, notes), or add them to a Draft of the order. */
export function CreatePaperPanel({ open, count, tickedText, form, setForm, vendors, defaultVendorId, selectedTasks,
  drafts, busy, onCreate, onAddToDraft, onClose }) {
  const over = vendorOverride(selectedTasks, form.vendorId, vendors);
  return (
    <FullScreenPanel
      open={open}
      title={`Make the paper (${count} job${count === 1 ? "" : "s"})`}
      onClose={busy ? undefined : onClose}
      testId="phone-out-create"
      footer={
        <button type="button" className="btn btn-primary btn-lg w-100" disabled={busy || !form.vendorId} onClick={onCreate}>
          {busy ? "Making the paper…" : "Make the paper"}
        </button>
      }
    >
      <div className="jq-out-form">
        {tickedText ? <div className="jq-note jq-note-info">{tickedText}</div> : null}
        <div className="mb-3">
          <label className="form-label" htmlFor="phone-out-vendor">Vendor <span className="text-danger">*</span></label>
          <select id="phone-out-vendor" className="form-select" value={form.vendorId}
            onChange={(e) => setForm((f) => ({ ...f, vendorId: e.target.value }))}>
            <option value="">-- pick the coater --</option>
            {vendors.map((v) => <option key={v.vendor_id} value={String(v.vendor_id)}>{v.name}</option>)}
          </select>
          {defaultVendorId != null ? <div className="form-text">From the parts' default vendor. One paper is one vendor.</div> : null}
          {over ? (
            <div className="jq-note jq-note-warn mt-2 mb-0">
              {over.count} job{over.count !== 1 ? "s" : ""} default{over.count === 1 ? "s" : ""} to {over.names.join(", ") || "another vendor"}:
              this paper sends {over.count === 1 ? "it" : "them"} to {over.chosenName} instead.
            </div>
          ) : null}
        </div>
        <div className="mb-3">
          <label className="form-label" htmlFor="phone-out-due">Due back (optional)</label>
          <input id="phone-out-due" type="date" className="form-control" value={form.dueBack}
            onChange={(e) => setForm((f) => ({ ...f, dueBack: e.target.value }))} />
        </div>
        <div className="mb-3">
          <label className="form-label" htmlFor="phone-out-notes">Notes to vendor (optional)</label>
          <textarea id="phone-out-notes" className="form-control" rows={3} value={form.notes}
            placeholder="Prints on the paper for the coater"
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
        </div>
        {drafts.length ? (
          <div className="jq-card">
            <div className="jq-section-title mb-2">Or add them to a paper being loaded</div>
            <select className="form-select mb-2" aria-label="Paper to add to" value={form.draftId}
              onChange={(e) => setForm((f) => ({ ...f, draftId: e.target.value }))}>
              {drafts.map((o) => (
                <option key={o.opo_id} value={String(o.opo_id)}>
                  {o.opo_number} · {o.vendor_name || "no vendor"} · {o.line_count} line{o.line_count !== 1 ? "s" : ""}
                </option>
              ))}
            </select>
            <button type="button" className="btn btn-outline-primary btn-lg w-100" disabled={busy || !form.draftId} onClick={onAddToDraft}>
              Add to that paper
            </button>
          </div>
        ) : null}
      </div>
    </FullScreenPanel>
  );
}

/** Receive back: what came back now per line (additive), scrapped, or close the line short. */
export function ReceivePanel({ opo, rows, setRows, busy, onReceive, onClose }) {
  if (!opo) return null;
  const set = (id, patch) => setRows((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), ...patch } }));
  return (
    <FullScreenPanel
      open
      title={`Receive ${opo.opo_number}`}
      onClose={busy ? undefined : onClose}
      testId="phone-out-receive"
      footer={
        <button type="button" className="btn btn-success btn-lg w-100" disabled={busy} onClick={onReceive}>
          {busy ? "Receiving…" : "Receive back"}
        </button>
      }
    >
      <div className="jq-out-form">
        <p className="small jq-muted">
          {opo.vendor_name ? `${opo.vendor_name}. ` : ""}Count what came back <strong>now</strong>. A line completes its
          task once returned + scrapped covers what was sent.
        </p>
        {(opo.lines || []).map((l) => {
          const row = rows[l.opo_line_id] || { ret: "0", scr: "0", close: false };
          const remaining = lineRemaining(l);
          return (
            <div className="jq-out-recv" key={l.opo_line_id} data-testid={`phone-out-recv-${l.opo_line_id}`}>
              <div><LineIdentity l={l} /></div>
              <div className="small jq-muted">
                Sent {fmtQty(l.quantity_sent)} · back {fmtQty(lineBack(l))}
                {Number(l.quantity_scrapped || 0) > 0 ? ` (${fmtQty(l.quantity_scrapped)} scrap)` : ""}
                {remaining === 0 ? " · complete" : ""}
              </div>
              {remaining > 0 ? (
                <>
                  <div className="jq-out-recv-inputs">
                    <label className="small mb-0">Received now
                      <input type="number" inputMode="decimal" className="form-control" min={0} max={remaining} step="any"
                        aria-label={`Received now, line ${l.line_number}`} value={row.ret}
                        onChange={(e) => set(l.opo_line_id, { ret: e.target.value })} />
                    </label>
                    <label className="small mb-0">Scrapped now
                      <input type="number" inputMode="decimal" className="form-control" min={0} max={remaining} step="any"
                        aria-label={`Scrapped now, line ${l.line_number}`} value={row.scr}
                        onChange={(e) => set(l.opo_line_id, { scr: e.target.value })} />
                    </label>
                  </div>
                  <label className="jq-out-check small">
                    <input type="checkbox" checked={!!row.close} onChange={(e) => set(l.opo_line_id, { close: e.target.checked })} />
                    <span>Close short: the vendor kept or lost the rest (booked as scrap so the task completes)</span>
                  </label>
                </>
              ) : null}
            </div>
          );
        })}
      </div>
    </FullScreenPanel>
  );
}
